/**
 * 메일 <b>새 창</b> 전용 페이지 — 읽기 전용.
 *
 * <p>목록 옆 모달로 보던 메일을 별도 브라우저 창으로 띄운다. 여러 건을 동시에 펼쳐 놓고
 * 비교하거나, 큰 화면으로 첨부·표를 보려는 요구다.
 *
 * <h3>인증은 따로 하지 않는다</h3>
 * <p>토큰이 {@code localStorage} 에 있고 localStorage 는 같은 오리진의 모든 창이 공유한다.
 * 이미 로그인한 브라우저에서 새 창을 열면 그대로 인증된 상태다.
 *
 * <h3>왜 읽기 전용인가</h3>
 * <p>답장·이동·삭제 같은 동작은 성공 후 <b>목록을 갱신</b>해야 하는데(폴더 배지, 차단 규칙
 * 소급 이동 등) 새 창에는 갱신할 목록이 없다. 그 갱신 로직은 {@code MailPage} 가 들고 있고,
 * 여기서 쓰려면 훅으로 뽑아 두 화면이 공유하도록 {@code MailPage}(2,700줄)를 수술해야 한다.
 * 새 창의 목적은 «크게 읽기» 라 거기까지 갈 이유가 없다.
 *
 * <p>차단은 {@code readOnly} 플래그다. 콜백을 안 넘기면 버튼이 알아서 빠질 것 같지만 <b>아니다</b> —
 * MailDetail 은 콜백 유무와 무관하게 버튼을 그리고 onClick 만 undefined 가 된다. 그러면 눌러도
 * 아무 일이 없어 «먹통» 으로 보인다. 실제로 그 상태로 한 번 띄웠다가 고쳤다.
 *
 * <p>읽음 처리만 예외다. 열었는데 안 읽음으로 남아 있으면 원래 창에서 계속 굵게 보인다.
 *
 * <h3>레이아웃 바깥 라우트다</h3>
 * <p>{@code App.js} 의 사이드바 레이아웃 <b>밖</b>에 등록한다. 안에 두면 새 창에도 전역 사이드바가
 * 딸려와 정작 메일이 좁아진다.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import { mailApi } from "./api/mailApi";
import MailDetail from "./MailDetail";
import LoadingDots from "../../component/common/LoadingDots";

export default function MailViewWindow() {
    const { id } = useParams();
    const [mail, setMail]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        mailApi.getDetail(id)
            .then((res) => {
                const d = res?.data ?? res;
                if (!alive) return;
                setMail(d);
                document.title = d?.subject || "메일";
                // 읽음 처리 — 실패해도 화면은 그대로 쓴다(원래 창에서 다시 읽으면 처리된다).
                mailApi.markRead(id).catch(() => { /* noop */ });
            })
            .catch((e) => {
                if (alive) setError(e?.response?.data?.message || "메일을 불러오지 못했습니다.");
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <LoadingDots label="메일을 불러오고 있어요" />
            </Box>
        );
    }

    if (error || !mail) {
        return (
            <Box sx={{ height: "100vh", display: "flex", flexDirection: "column",
                       alignItems: "center", justifyContent: "center", gap: 1, color: "#64748b" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{error || "메일을 찾을 수 없습니다."}</Typography>
                <Typography sx={{ fontSize: 12.5 }}>이 창을 닫고 목록에서 다시 열어 주세요.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
            {/* readOnly 가 동작 버튼(답장·삭제·이동·차단·별표)과 최대화를 함께 내린다.
                남는 것은 인쇄와 닫기뿐이다. */}
            <MailDetail
                mail={mail}
                folder={mail?.folder}
                onClose={() => window.close()}
                readOnly
            />
        </Box>
    );
}
